<script lang="ts">
  import { createQuery, useQueryClient } from '@tanstack/svelte-query';
  import { ChevronDown, Plus } from 'lucide-svelte';
  import { hrRequestsApi, type HRRequestBody } from '$lib/api/hrRequests';
  import { leaveSummaryApi } from '$lib/api/leaveSummary';
  import { getErrorMessage } from '$lib/api/client';
  import { toast } from '$lib/state/toast.svelte';
  import { businessDays, rangeHints } from '$lib/utils/leave';
  import Modal from '$lib/components/ui/Modal.svelte';
  import Toolbar from '$lib/components/ui/Toolbar.svelte';
  import DataTable from '$lib/components/ui/DataTable.svelte';
  import StatusBadge, { type BadgeKind } from '$lib/components/ui/StatusBadge.svelte';
  import RowActions from '$lib/components/ui/RowActions.svelte';
  import { t } from '$lib/i18n';
  import type { MessageKey } from '$lib/i18n/en';
  import type { HRRequest, HRRequestType } from '$lib/types';

  const queryClient = useQueryClient();
  const query = createQuery(() => ({
    queryKey: ['hr-requests'],
    queryFn: () => hrRequestsApi.list()
  }));
  // Backs the leave-type picker's "Casual — 7 of 12 left" options — same
  // data the Dashboard's "Your leave" card reads (GET /api/leave/summary).
  const leaveSummaryQuery = createQuery(() => ({
    queryKey: ['leave-summary'],
    queryFn: () => leaveSummaryApi.get()
  }));

  function refresh() {
    queryClient.invalidateQueries({ queryKey: ['hr-requests'] });
  }

  const TYPE_LABEL_KEY: Record<HRRequestType, MessageKey> = {
    leave: 'requests.type.leave',
    late: 'requests.type.late',
    early_leaving: 'requests.type.early_leaving',
    other: 'requests.type.other'
  };

  const STATUS_LABEL_KEY: Record<string, MessageKey> = {
    pending: 'requests.status.pending',
    approved: 'requests.status.approved',
    rejected: 'requests.status.rejected',
    logged: 'requests.status.logged'
  };

  const STATUS_KIND: Record<string, BadgeKind> = {
    pending: 'pending',
    approved: 'approved',
    rejected: 'rejected',
    logged: 'logged'
  };

  const REASON_CATEGORIES = ['sick', 'personal', 'vacation', 'family', 'bereavement', 'menstrual', 'other'] as const;

  const emptyForm: HRRequestBody = {
    type: 'leave',
    start_date: '',
    end_date: '',
    half_day: false,
    reason_category: '',
    reason: ''
  };

  let editing = $state<HRRequest | null>(null);
  let showForm = $state(false);
  let form = $state<HRRequestBody>({ ...emptyForm });
  let saving = $state(false);
  let error = $state('');

  const previewDays = $derived(
    form.type === 'leave' && form.start_date && form.end_date
      ? businessDays(form.start_date, form.end_date, !!form.half_day)
      : null
  );
  const hints = $derived(
    form.type === 'leave' ? rangeHints(form.start_date, form.end_date ?? '', !!form.half_day) : []
  );

  // "Casual — 7 of 12 left" style option labels, from GET /api/leave/summary
  // (active types only) — shows the balance right where the employee picks.
  function leaveTypeLabel(lt: { name: string; remaining: number; quota: number }): string {
    return t('requests.leaveTypeLabel', { name: lt.name, remaining: lt.remaining, quota: lt.quota });
  }

  function openCreate() {
    editing = null;
    form = { ...emptyForm };
    error = '';
    showForm = true;
  }

  function openEdit(r: HRRequest) {
    editing = r;
    form = {
      type: r.type,
      start_date: r.start_date.slice(0, 10),
      end_date: r.end_date?.slice(0, 10) ?? '',
      half_day: r.half_day,
      reason_category: r.reason_category ?? '',
      reason: r.reason,
      leave_type_id: r.leave_type_id ?? undefined
    };
    error = '';
    showForm = true;
  }

  function editable(status: string): boolean {
    return status === 'pending' || status === 'logged';
  }

  async function save() {
    if (form.type === 'leave' && (!form.start_date || !form.end_date)) {
      error = t('requests.error.datesRequired');
      return;
    }
    // Required on new leave requests only (see buildLeaveRequest in
    // hr_requests.go) — without it, an approved request's ledger write never
    // fires and the employee's balance never moves. Menstrual leave is
    // assigned server-side; the client must not require a type pick.
    if (form.type === 'leave' && !editing && form.reason_category !== 'menstrual' && !form.leave_type_id) {
      error = t('requests.error.leaveTypeRequired');
      return;
    }
    if ((form.type === 'late' || form.type === 'early_leaving') && !form.start_date) {
      error = t('requests.error.dateRequired');
      return;
    }
    if (!form.reason.trim()) {
      error = t('requests.error.reasonRequired');
      return;
    }
    saving = true;
    error = '';
    try {
      if (editing) {
        await hrRequestsApi.update(editing.id, form);
        toast.success(t('requests.toast.updated'));
      } else {
        await hrRequestsApi.create(form);
        toast.success(t('requests.toast.submitted'));
      }
      refresh();
      showForm = false;
    } catch (err) {
      error = getErrorMessage(err);
    } finally {
      saving = false;
    }
  }

  async function remove(id: number) {
    if (!confirm(t('requests.confirmDelete'))) return;
    try {
      await hrRequestsApi.remove(id);
      refresh();
      toast.success(t('requests.toast.deleted'));
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  }
</script>

<div class="tym-page">
  <Toolbar>
    {#snippet actions()}
      <button type="button" class="btn-primary" onclick={openCreate}>
        <Plus class="h-4 w-4" /> {t('requests.new')}
      </button>
    {/snippet}
  </Toolbar>

  {#if query.isLoading}
    <div class="tym-loading-state">{t('common.loading')}</div>
  {:else if query.isError}
    <div class="tym-error-state">{t('requests.error')}</div>
  {:else if query.data}
    <DataTable
      mobileCards
      columns={[
        { key: 'type', label: t('requests.type'), cell: typeCell },
        { key: 'dates', label: t('requests.date'), cell: datesCell },
        { key: 'status', label: t('requests.status'), cell: statusCell },
        { key: 'reason', label: t('requests.reason'), cell: reasonCell },
        { key: 'actions', label: '', align: 'right' as const, cell: actionsCell }
      ]}
      rows={query.data}
      rowKey={(r) => r.id}
      emptyTitle={t('requests.empty')}
      mobileCard={requestCard}
    />
  {/if}

  {#if showForm}
    <Modal title={editing ? t('requests.edit') : t('requests.new')} onClose={() => (showForm = false)}>
      <div class="space-y-4">
        <div>
          <div class="label">{t('requests.type')}</div>
          <select class="input" bind:value={form.type} disabled={!!editing}>
            <option value="leave">{t('requests.type.leave')}</option>
            <option value="late">{t('requests.type.late')}</option>
            <option value="early_leaving">{t('requests.type.early_leaving')}</option>
            <option value="other">{t('requests.type.other')}</option>
          </select>
        </div>

        {#if form.type === 'leave'}
          <div class="grid grid-cols-2 gap-3">
            <div>
              <div class="label">{t('requests.startDate')}</div>
              <input type="date" class="input" bind:value={form.start_date} />
            </div>
            <div>
              <div class="label">{t('requests.endDate')}</div>
              <input type="date" class="input" bind:value={form.end_date} />
            </div>
          </div>
          <label class="flex items-center gap-2 text-[13px] text-toyama-heavy-carbon">
            <input type="checkbox" bind:checked={form.half_day} class="h-4 w-4 accent-toyama-teal" />
            {t('requests.halfDay')}
          </label>
          {#if !editing && form.reason_category !== 'menstrual'}
            <div>
              <div class="label">{t('requests.leaveType')}</div>
              <select class="input" bind:value={form.leave_type_id}>
                <option value={undefined}>{t('requests.selectLeaveType')}</option>
                {#each leaveSummaryQuery.data?.types ?? [] as lt (lt.leave_type_id)}
                  <option value={lt.leave_type_id}>{leaveTypeLabel(lt)}</option>
                {/each}
              </select>
            </div>
          {/if}
          <div>
            <div class="label">{t('requests.reasonCategory')}</div>
            <select class="input" bind:value={form.reason_category}>
              <option value="">{t('requests.selectCategory')}</option>
              {#each REASON_CATEGORIES as cat (cat)}
                {#if cat !== 'menstrual' || leaveSummaryQuery.data?.gender === 'female'}
                  <option value={cat}>{t(`requests.category.${cat}`)}</option>
                {/if}
              {/each}
            </select>
          </div>
          {#if previewDays !== null}
            <p class="text-[12.5px] text-toyama-teal">{t('requests.businessDays', { n: previewDays })}</p>
          {/if}
          {#each hints as h (h)}
            <p class="text-[12px] text-[#9a6300]">{t(h)}</p>
          {/each}
        {:else if form.type === 'late' || form.type === 'early_leaving'}
          <div>
            <div class="label">{t('requests.date')}</div>
            <input type="date" class="input" bind:value={form.start_date} />
          </div>
          {#if form.type === 'late'}
            <p class="text-[12px] text-dark-gray">{t('requests.lateHint')}</p>
          {/if}
        {/if}

        <div>
          <div class="label">{t('requests.reason')}</div>
          <textarea class="input" rows="3" bind:value={form.reason}></textarea>
        </div>

        {#if error}<div class="tym-error-state">{error}</div>{/if}
        <div class="flex justify-end gap-2 pt-1">
          <button type="button" class="btn-secondary" onclick={() => (showForm = false)}>{t('common.cancel')}</button>
          <button type="button" class="btn-primary" disabled={saving} onclick={save}>
            {saving ? t('common.submitting') : t('common.submit')}
          </button>
        </div>
      </div>
    </Modal>
  {/if}
</div>

{#snippet typeCell(r: HRRequest)}
  <span class="font-medium text-navy">{t(TYPE_LABEL_KEY[r.type])}{r.leave_type_name ? ` — ${r.leave_type_name}` : ''}</span>
{/snippet}
{#snippet datesCell(r: HRRequest)}
  <span class="muted"
    >{r.start_date.slice(0, 10)}{r.end_date && r.end_date.slice(0, 10) !== r.start_date.slice(0, 10)
      ? ` – ${r.end_date.slice(0, 10)}`
      : ''}</span
  >
{/snippet}
{#snippet statusCell(r: HRRequest)}
  <StatusBadge kind={STATUS_KIND[r.status]} label={t(STATUS_LABEL_KEY[r.status])} />
{/snippet}
{#snippet reasonCell(r: HRRequest)}
  <span class="muted">{r.reason || '—'}</span>
{/snippet}
{#snippet actionsCell(r: HRRequest)}
  {#if editable(r.status)}
    <RowActions items={[{ label: t('common.edit'), onClick: () => openEdit(r) }, { label: t('common.delete'), onClick: () => remove(r.id), danger: true }]} />
  {/if}
{/snippet}

{#snippet requestCard(r: HRRequest)}
  <details class="tym-disclosure w-full min-w-0">
    <summary class="flex cursor-pointer items-start justify-between gap-3">
      <span class="min-w-0 flex-1">
        <span class="block truncate text-[13.5px] font-medium text-navy">
          {t(TYPE_LABEL_KEY[r.type])}{r.leave_type_name ? ` — ${r.leave_type_name}` : ''}
        </span>
        <span class="mt-1 flex flex-wrap items-center gap-2 text-[12px] text-dark-gray">
          {@render datesCell(r)}
          {@render statusCell(r)}
        </span>
      </span>
      <span class="inline-flex shrink-0 items-center gap-1 text-[12px] font-medium text-toyama-teal">
        {t('requests.fullRequest')}
        <ChevronDown class="tym-disclosure-chevron h-4 w-4" />
      </span>
    </summary>

    <div class="mt-3 border-t border-toyama-cloudy-gray/60 pt-3 text-[13px]">
      {#if r.reason_category}
        <p class="text-[11px] font-semibold uppercase tracking-[.06em] text-dark-gray">
          {t('requests.reasonCategory')}
        </p>
        <p class="mt-0.5 text-toyama-heavy-carbon">{t(`requests.category.${r.reason_category}`)}</p>
      {/if}
      {#if r.type === 'leave'}
        <div class="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-dark-gray">
          {#if r.business_days != null}<span>{t('requests.businessDays', { n: r.business_days })}</span>{/if}
          {#if r.half_day}<span>{t('requests.halfDay')}</span>{/if}
        </div>
      {/if}
      <p class="mt-2 text-[11px] font-semibold uppercase tracking-[.06em] text-dark-gray">{t('requests.reason')}</p>
      <p class="mt-0.5 whitespace-pre-wrap break-words text-toyama-heavy-carbon">{r.reason || '—'}</p>
      {#if editable(r.status)}
        <div class="mt-3 flex justify-end">{@render actionsCell(r)}</div>
      {/if}
    </div>
  </details>
{/snippet}
